import { reskinStyles } from 'core/components/theme/theme-variant';
import { makeStyles } from '@material-ui/core/styles';

const styles = (theme) => ({
  // A row whose status the user just changed lands in its new status card with a green tint that
  // holds while the row scrolls into view (use-reveal-on-status-change.js), then fades to the row's
  // resting background; its status icon fades in.
  '@keyframes statusChangedTint': {
    '0%, 40%': { backgroundColor: theme.colorPalette.green_75_op_30 }
  },
  '@keyframes statusIconIn': {
    from: { opacity: 0, transform: 'scale(0.95)' }
  },
  statusChanged: {
    animation: '$statusChangedTint 1.6s ease',
    // Where the row is scrolled to: clear of the viewport's top edge, and above the success toast
    // that announces the change (toast.styles.js places it 120px from the bottom).
    scrollMarginTop: `${theme.spacing(8)}px`,
    scrollMarginBottom: `${theme.spacing(50)}px`,
    '& $completedIcon, & $pendingIcon': {
      animation: '$statusIconIn 200ms cubic-bezier(0.23, 1, 0.32, 1)'
    },
    // Reduced motion: the row simply appears in its new card.
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      '& $completedIcon, & $pendingIcon': {
        animation: 'none'
      }
    }
  },
  container: {
    borderTop: `solid 1px ${theme.colorPalette.beige_50}`,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    padding: theme.spacing(4, 8),
    position: 'relative'
  },
  updating: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: theme.colorPalette.green_80,
    '@media (prefers-reduced-motion: reduce)': {
      '& .MuiCircularProgress-root, & .MuiCircularProgress-circle': {
        animation: 'none'
      }
    }
  },
  completed: {
    backgroundColor: theme.colorPalette.beige_25
  },
  task: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  taskInfo: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
    width: '100%'
  },
  taskInfoCompleted: {
    gridTemplateColumns: '2fr 1fr'
  },
  taskName: {
    whiteSpace: 'nowrap',
    color: theme.colorPalette.green_80,
    lineHeight: 1.5
  },
  completedAt: {
    fontSize: 10,
    color: theme.colorPalette.green_80
  },
  completedTask: {
    color: theme.colorPalette.beige_75,
    // Reskin: beige75 on white reads as disabled (about 2:1); the Approved section already marks it done.
    ...reskinStyles(theme, { color: theme.colors.text_primary })
  },
  tags: {
    marginTop: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1)
  },
  upload: {
    fontSize: 12,
    fontWeight: 600
  },
  uploadIcon: {
    marginLeft: theme.spacing(2)
  },
  completedIcon: {
    height: 24,
    margin: 'auto 0',
    '--completed-icon-stroke': theme.colorPalette.green_legacy_86c440
  },
  pendingIcon: {
    height: '24px',
    width: '24px',
    margin: 'auto 0'
  },
  progressBar: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0
  },
  filesTooltip: {
    position: 'absolute',
    backgroundColor: 'white',
    borderRadius: theme.sizes.roundCorners,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
  },
  fileName: {
    padding: theme.spacing(4),
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.colorPalette.beige_25
    }
  },
  taskNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  learnMoreLink: {
    width: 'auto',
    height: 'auto',
    borderRadius: theme.spacing(2),
    color: theme.colors.action_text,
    padding: 0,
    marginLeft: theme.spacing(0.2),
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  learnMoreLinkText: {
    ...reskinStyles(theme, theme.actionStyles.link),
    fontSize: 10,
    fontWeight: 400,
    color: theme.colors.action_text
  },
  iconButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1)
  },
  actionButton: {
    color: theme.colors.action_text,
    fontSize: 12,
    fontWeight: 600
  },
  actionButtonIcon: {
    marginRight: theme.spacing(2)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  tag: {
    fontWeight: 400,
    color: theme.colorPalette.green_90,
    backgroundColor: theme.colorPalette.beige_50,
    '& > img': {
      height: 14,
      margin: theme.spacing(0, 1)
    },
    ...reskinStyles(theme, { ...theme.labelStyles.outlined, '& > img': { height: 14, margin: 0 } })
  },
  statusTag: {
    backgroundColor: 'white',
    ...reskinStyles(theme, { backgroundColor: 'transparent' })
  },
  inlineTaskDescription: {
    display: 'block',
    margin: 4,
    lineHeight: 1.5
  },
  questionIcon: {
    height: 12,
    padding: theme.spacing(0, 0.5)
  },
  approvalSection: {
    width: '185%',
    maxWidth: '650px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(4),
    margin: theme.spacing(4, 0),
    backgroundColor: theme.colorPalette.beige_50,
    borderRadius: theme.spacing(2),
    '& > img': {
      height: 16,
      marginTop: theme.spacing(0.5),
      alignSelf: 'flex-start'
    }
  },
  approvalContent: {
    marginLeft: theme.spacing(2),
    '& > first-child': {
      marginBottom: theme.spacing(2)
    }
  },
  approvalButton: {
    backgroundColor: 'black',
    color: 'white',
    padding: theme.spacing(2),
    '&:hover': {
      backgroundColor: 'black',
      boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)'
    },
    '& > img': {
      marginRight: theme.spacing(2)
    }
  },
  approvalButtonLoading: {
    minWidth: 160,
    pointerEvents: 'none'
  },
  fileMenuItem: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  removeIcon: {
    height: 24,
    width: 24,
    marginRight: theme.spacing(0.5)
  }
});

export default makeStyles(styles);
